import { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '@/lib/auth-store';
import { cn } from '@/lib/cn';
import { CheckCircle2, Edit3, Locate, RefreshCw, AlertTriangle, Map, X } from 'lucide-react';
import type { EPCR } from '@/types';
import {
  computeMileage, commitMileage,
  startBreadcrumbCollection, stopBreadcrumbCollection,
  isBreadcrumbCollectionActive,
  type MileageResult,
} from '@/lib/mileage-service';

/**
 * TransportMileage — billing-accurate mileage capture per addendum § 3.
 *
 * Design: one-tap confirmation for the 90% case, one tap to "Adjust"
 * for edge cases. The crew never has to pick between GPS / mapped /
 * manual under time pressure — the system picks the most defensible
 * value silently, the crew just taps Confirm.
 *
 * Shown inside the Disposition tab once a transport decision is set.
 * If the decision is NOT a transport (refusal, no-patient, treat-and-
 * release), the component renders a small "no mileage — not a transport"
 * note so it doesn't vanish from the UI entirely.
 */

interface Props {
  epcr: EPCR;
  incidentId: string;
  /** Destination coords if known — powers the Mapped Route fallback. */
  sceneLatLng?: { lat: number; lng: number };
  destinationLatLng?: { lat: number; lng: number };
}

export function TransportMileage({ epcr, incidentId, sceneLatLng, destinationLatLng }: Props) {
  const { user } = useAuthStore();
  const transported = epcr.transport_decision === 'transport_this_unit'
    || epcr.transport_decision === 'transfer_other_unit';

  const [result, setResult] = useState<MileageResult | null>(null);
  const [manual, setManual] = useState<string>(
    epcr.mileage_manual_entry !== null ? String(epcr.mileage_manual_entry) : '',
  );
  const [loading, setLoading] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [breadcrumbActive, setBreadcrumbActive] = useState(
    () => isBreadcrumbCollectionActive(epcr.id),
  );

  async function recompute() {
    setLoading(true);
    try {
      const r = await computeMileage(epcr.id, {
        sceneLatLng, destinationLatLng,
        priorManual: manual ? Number(manual) : null,
      });
      setResult(r);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void recompute(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [epcr.id]);

  function startCollection() {
    if (!user) return;
    startBreadcrumbCollection({
      epcrId: epcr.id,
      incidentId,
      agencyId: user.agency_id,
      phase: 'loaded_en_route_hospital',
    });
    setBreadcrumbActive(true);
  }
  function stopCollection() {
    stopBreadcrumbCollection(epcr.id);
    setBreadcrumbActive(false);
  }

  const alreadyConfirmed = !!epcr.mileage_confirmed_at;

  async function confirm(source: 'gps_actual' | 'mapped_route' | 'manual', value: number, overrideReason?: string) {
    if (!user) return;
    await commitMileage({
      epcrId: epcr.id,
      userId: user.id,
      candidates: {
        gps_actual: result?.gps_actual ?? null,
        mapped_route: result?.mapped_route ?? null,
        manual_entry: manual ? Number(manual) : null,
      },
      submitted: value,
      source,
      overrideReason,
    });
    await recompute();
  }

  if (!transported) {
    return (
      <div className="border border-slate-700 bg-slate-900/50 px-3 py-2 text-xs text-slate-400">
        <div className="flex items-center gap-1.5">
          <Map size={13} className="text-slate-500" />
          <span className="font-bold uppercase tracking-widest text-slate-300">Loaded Mileage (HCPCS A0425)</span>
        </div>
        <div className="mt-1 text-slate-500">Not a transport disposition — no billable mileage to capture.</div>
      </div>
    );
  }

  return (
    <section className="border border-blue-700/40 bg-blue-950/10">
      <div className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-2.5 py-1.5">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-300">
          <Map size={13} className="text-blue-400" />
          Loaded Mileage · HCPCS A0425
        </div>
        {alreadyConfirmed ? (
          <span className="status-pill bg-emerald-950 text-emerald-300 border border-emerald-800">
            Confirmed
          </span>
        ) : (
          <span className="status-pill bg-amber-950 text-amber-300 border border-amber-800">
            Pending
          </span>
        )}
      </div>

      {/* Breadcrumb state strip */}
      <div className="flex items-center justify-between gap-2 border-b border-slate-800 bg-slate-950 px-2.5 py-1.5">
        <div className="flex items-center gap-1.5 text-[11px]">
          <Locate size={12} className={breadcrumbActive ? 'text-emerald-400' : 'text-slate-500'} />
          {breadcrumbActive ? (
            <span className="text-emerald-300">
              Recording loaded-phase GPS trail ({result?.loaded_phase_point_count ?? 0} pts)
            </span>
          ) : (
            <span className="text-slate-400">
              GPS trail not active. Start it at patient loading for best accuracy.
            </span>
          )}
        </div>
        <div className="flex gap-1">
          {breadcrumbActive
            ? <button onClick={stopCollection} className="text-[10px] px-2 py-0.5 border border-red-700 text-red-300 hover:bg-red-950/40">Stop</button>
            : <button onClick={startCollection} className="text-[10px] px-2 py-0.5 border border-emerald-700 text-emerald-300 hover:bg-emerald-950/40">Start GPS</button>}
          <button onClick={recompute} className="text-[10px] px-2 py-0.5 border border-slate-600 text-slate-300 hover:bg-slate-800" title="Recompute mileage">
            <RefreshCw size={10} />
          </button>
        </div>
      </div>

      {/* The ONE presented value — addendum § 3.2 */}
      <div className="p-3">
        {loading && !result ? (
          <div className="text-sm text-slate-500">Computing…</div>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-baseline gap-1">
              <span className="font-mono tabular-nums text-4xl font-black text-slate-100">
                {formatMiles(epcr.loaded_mileage_miles ?? result?.submitted_miles)}
              </span>
              <span className="text-sm text-slate-400">mi</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                Source
              </div>
              <div className="text-xs text-slate-300 font-mono">
                {labelSource(epcr.mileage_source ?? result?.submitted_source)}
              </div>
            </div>
            {!alreadyConfirmed && result?.submitted_miles !== null && result?.submitted_miles !== undefined && result?.submitted_source ? (
              <button
                onClick={() => confirm(result.submitted_source!, result.submitted_miles!)}
                className="btn-primary gap-1 text-sm"
              >
                <CheckCircle2 size={14} />
                Confirm
              </button>
            ) : null}
            <button onClick={() => setAdjustOpen(true)} className="btn-secondary text-xs gap-1">
              <Edit3 size={12} />
              Adjust
            </button>
          </div>
        )}

        {/* Audit bundle — preserved per § 3.3 */}
        {result && (
          <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
            <AuditCell label="GPS Actual"   value={result.gps_actual}   hint={`${result.loaded_phase_point_count} loaded pts`} />
            <AuditCell label="Mapped Route" value={result.mapped_route} hint="straight-line fallback" />
            <AuditCell label="Manual"       value={result.manual_entry} hint="crew entry" />
          </div>
        )}

        {alreadyConfirmed && epcr.mileage_override_reason && (
          <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-300">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <span>
              Override reason: <span className="italic">{epcr.mileage_override_reason}</span>
            </span>
          </div>
        )}
      </div>

      {/* Adjust overlay */}
      {adjustOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3">
          <div className="w-full max-w-md border border-slate-700 bg-slate-900 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-700 bg-slate-950 px-3 py-1.5">
              <span className="text-[11px] font-bold uppercase tracking-widest text-slate-200">
                Adjust Loaded Mileage
              </span>
              <button onClick={() => setAdjustOpen(false)} className="text-slate-500 hover:text-slate-200">
                <X size={14} />
              </button>
            </div>
            <div className="p-3 space-y-2">
              <p className="text-[11px] text-slate-400 leading-snug">
                Use this only for edge cases — construction detour, equipment decon mid-transport,
                hospital diversion, or when GPS dropped. The adjusted value + your reason are
                preserved in the audit trail.
              </p>
              <label className="block">
                <span className="data-label-dense">Adjusted Miles</span>
                <input
                  className="input-field"
                  inputMode="decimal"
                  value={manual}
                  onChange={(e) => setManual(e.target.value.replace(/[^\d.]/g, ''))}
                  placeholder="e.g. 4.7"
                />
              </label>
              <label className="block">
                <span className="data-label-dense">Reason (required)</span>
                <input
                  id="adjust-reason"
                  className="input-field"
                  placeholder="Construction detour / decon / diversion / GPS loss"
                />
              </label>
              <div className="flex gap-2 justify-end pt-1">
                <button onClick={() => setAdjustOpen(false)} className="btn-secondary text-xs">Cancel</button>
                <button
                  disabled={!manual}
                  onClick={() => {
                    const reason = (document.getElementById('adjust-reason') as HTMLInputElement | null)?.value ?? '';
                    if (!manual || !reason.trim()) return;
                    void confirm('manual', Number(manual), reason.trim()).then(() => setAdjustOpen(false));
                  }}
                  className="btn-primary text-xs gap-1"
                >
                  <CheckCircle2 size={12} />
                  Save Override
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function AuditCell({ label, value, hint }: { label: string; value: number | null; hint: string }) {
  return (
    <div className="border border-slate-800 bg-slate-900 px-2 py-1">
      <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-mono tabular-nums text-sm text-slate-200">
        {value === null ? '—' : `${value.toFixed(1)} mi`}
      </div>
      <div className="text-[9px] text-slate-600 truncate">{hint}</div>
    </div>
  );
}

function labelSource(s: 'gps_actual' | 'mapped_route' | 'manual' | null | undefined): string {
  switch (s) {
    case 'gps_actual':   return 'GPS Actual (haversine)';
    case 'mapped_route': return 'Mapped Route (straight-line fallback)';
    case 'manual':       return 'Manual Entry';
    default:             return 'Not yet set';
  }
}

function formatMiles(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  // Medicare precision: tenth-of-mile under 100, whole number at 100+
  if (n >= 100) return String(Math.floor(n));
  return n.toFixed(1);
}
